import {useMemo} from 'react'
import {
  Box, Select, Button, Flex, Icon, Text
} from '@binance/uikit'
import {floor} from 'utils/number'
import {
  LIMIT_TYPES, TIME_IN_FORCE
} from 'constants'
import useSymbolInfo from 'hooks/useSymbolInfo'
import Radio from 'components/Radio'
import Checkbox from 'components/Checkbox'
import Tooltip from 'components/Tooltip'

const {Option} = Select

export default function TradeLimitType ({
  name,
  i18n = {},
  limitType,
  setLimitType,
  setTypeInForce,
  availableBalance,
  isLimit = true,
  reduceOnly,
  enableReduceOnly,
}) {
  const {symbolInfo} = useSymbolInfo()
  const {quoteAsset, pricePrecision} = symbolInfo

  return useMemo(
    () => (
      <Flex justifyContent="space-between" alignItems="center">
        <Box color="#848E9C">
          {
            isLimit && <>
              <Radio
                name={name}
                text={
                  <Tooltip
                    label={i18n['limitType-postOnly'] || 'Post Only'}
                    tip={
                      i18n['limitType-postOnly-tip']
                    || 'Post-Only order will be added to the order book and not execute with a pre-existing order immediately. Post-Only order is used to ensure the maker rebate.'
                    }
                  />
                }
                value={LIMIT_TYPES.POST_ONLY}
                checked={limitType === LIMIT_TYPES.POST_ONLY}
                onChange={() => setLimitType(LIMIT_TYPES.POST_ONLY)}
                style={{marginRight: '16px'}}
              />

            <Radio
              name={name}
              text={
                <Tooltip
                  label={i18n['limitType-TIF'] || 'TIF'}
                  tip={[
                    <Text.Base key="0" fontWeight={500} fontSize={13} mb={3}>
                      {i18n['timeInForce-title'] || 'Time in Force'}
                    </Text.Base>,
                    <Text.Base key="1" mb={2}>
                      {i18n['timeInForce-GTC'] || 'GTC: Good Till Cancel'}
                    </Text.Base>,
                    <Text.Base key="2" mb={2}>
                      {i18n['timeInForce-IOC'] || 'IOC: Immediate Or Cancel'}
                    </Text.Base>,
                    <Text.Base key="3" mb={2}>
                      {i18n['timeInForce-FOK'] || 'FOK: Fill Or Kill'}
                    </Text.Base>
                  ]}
                />
              }
              value={LIMIT_TYPES.TIF}
              checked={limitType === LIMIT_TYPES.TIF}
              onChange={() => setLimitType(LIMIT_TYPES.TIF)}
            />

            <Select
              optionStyle={{
                px: '8px',
                py: '8px',
                height: '30px',
                fontSize: '12px',
                ml: '6px'
              }}
              onChange={setTypeInForce}
              defaultValue={TIME_IN_FORCE.TIF.GTC}
              width="auto"
              cmpRender={val => (
                <Button
                  value={val}
                  glossy={false}
                  variant="small"
                  color={limitType === LIMIT_TYPES.TIF ? 'primary' : undefined}
                  disabled={limitType !== LIMIT_TYPES.TIF}
                >
                  {val}
                  <svg
                    style={{marginLeft: 4}}
                    width="6"
                    height="4"
                    viewBox="0 0 6 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.87006 0.855655L3.30346 3.85338C3.22681 3.9466 3.1163 4 3.00008 4C2.88394 4 2.77343 3.9466 2.69668 3.85338L0.130181 0.855655C0.00489473 0.714313 -0.0338278 0.508304 0.0309405 0.326341C0.0957088 0.144457 0.252008 0.0173411 0.433562 0L5.56455 0C5.7468 0.016602 5.90384 0.1429 5.96853 0.325602C6.03407 0.508304 5.99534 0.713574 5.87006 0.855655Z"
                      fill={`#${limitType === LIMIT_TYPES.TIF ? 'F0B90B' : '848E9C'}`}
                    />
                  </svg>
                </Button>
              )}
            >
              {limitType === LIMIT_TYPES.TIF
                ? Object.keys(TIME_IN_FORCE.TIF).map(k => (
                  <Option key={k} value={k}>
                    {k}
                  </Option>
                ))
                : null}
            </Select>
          </>
          }

          {enableReduceOnly && (
            <Checkbox
              text={
                <Tooltip
                  label={i18n['limitType-reduceOnly'] || 'Reduce Only'}
                  tip={
                    i18n['limitType-reduceOnly-tip']
                      || 'Reduce-Only order will be added to the order book and not execute with a pre-existing order immediately. Post-Only order is used to ensure the maker rebate.'
                  }
                />
              }
              checked={reduceOnly}
              onChange={() => setLimitType(LIMIT_TYPES.REDUCE_ONLY)}
              style={{
                verticalAlign: 'top',
                ...(isLimit
                  ? {marginLeft: '16px', paddingLeft: '16px', borderLeft: '1px solid #48515D'} : {})
              }}
            />
          )}
        </Box>

        <Flex flex={1} />

        <Text.Base color="#848E9C" py="2px">
          <Icon color="#848E9C" size={14} name="wallet" mr={1} />
          {floor(availableBalance, pricePrecision, true)} {quoteAsset}
        </Text.Base>
      </Flex>
    ),
    [availableBalance, enableReduceOnly, i18n, isLimit, limitType, name, pricePrecision, quoteAsset, reduceOnly, setLimitType, setTypeInForce]
  )
}
